import { FC, useCallback, useRef, type TableHTMLAttributes } from 'react';
import { Tooltip } from 'react-tooltip';
import { useTranslation } from 'react-i18next';
import DownloadButton from '@/storybook/components/DownloadButton/DownloadButton';
import { useToast } from '@/storybook/components/Toast/Toast';
import { isElementTruncated } from '@/hooks/useIsTruncated';
import { CELL_TOOLTIP_ID } from './AgentTableCell';
import { generateCsvFromTable, downloadCsv } from './agentTableCsv';
import styles from './AgentTable.module.css';
import { AGENT_TABLE_TEST_IDS } from './testIds';

const TOOLTIP_ID = 'agent-table-download-tooltip';
const CELL_TOOLTIP_HIDE_DELAY_MS = 200;

const shouldDisableCellTooltip = (cellAnchor: HTMLElement | null): boolean =>
  cellAnchor === null || !isElementTruncated(cellAnchor);

const AgentTable: FC<TableHTMLAttributes<HTMLTableElement>> = ({ children, ...props }) => {
  const { t } = useTranslation();
  const { makeToast } = useToast();
  const tableRef = useRef<HTMLTableElement>(null);

  const handleDownload = useCallback(async () => {
    if (!tableRef.current) {
      return;
    }

    const csv = generateCsvFromTable(tableRef.current);
    downloadCsv(csv);
    makeToast({ content: String(t('main.download.success')), appearance: 'success' });
  }, [makeToast, t]);

  return (
    <div className={styles.wrapper} data-testid={AGENT_TABLE_TEST_IDS.wrapper}>
      <div data-tooltip-id={TOOLTIP_ID} data-tooltip-content={t('components.aiChatDrawer.agentTable.downloadCsv')}>
        <DownloadButton
          onDownload={handleDownload}
          className={styles.downloadButton}
          data-testid={AGENT_TABLE_TEST_IDS.downloadButton}
        />
      </div>
      <Tooltip id={TOOLTIP_ID} place="top" />
      <Tooltip
        id={CELL_TOOLTIP_ID}
        place="top"
        clickable
        offset={0}
        delayHide={CELL_TOOLTIP_HIDE_DELAY_MS}
        disableTooltip={shouldDisableCellTooltip}
        className={styles.cellTooltip}
      />
      <div className={styles.scrollContainer}>
        <table ref={tableRef} className={styles.table} data-testid={AGENT_TABLE_TEST_IDS.table} {...props}>
          {children}
        </table>
      </div>
    </div>
  );
};

export default AgentTable;
